/**
 * The Wrap-up card: what happens between "every task is done" and "the plan is closed", as five steps in order, each
 * with its state (done, running by <name>, waiting for you, not needed) and the one action it needs. The steps and
 * words are the workbench's (`wrapUpModel` and its planLoop wording, `@aioe/shared/models`); the facts come from the
 * wrap-up the workbench publishes at the home (planWrapUp.ts) and the plan record.
 *
 * Ported from the workbench's components/plans/WrapUpCard.tsx. Where the desktop sends a message to its agents, the
 * console files an ask on the wrap-up (`POST …/wrap-up/asks`) that the workbench carries out and then says it did, and
 * a criterion is verified or marked not met on the plan itself (`POST …/acceptance`). Reading the changes and merging
 * them stay on the desktop, so the review step says where it stands and offers only to skip it.
 */

import type { PlanWrapUpAsk, PlanWrapUpAskInput } from '@aioe/shared';
import { wrapUpAskWords } from '@aioe/shared';
import { UNCHECKABLE, WRAP_UP, type StepState, type WrapUpAcceptance, type WrapUpPlan, type WrapUpStep, wrapUpModel } from '@aioe/shared/models';
import { Check, ChevronDown, ChevronRight, CircleDashed, Hourglass, ListCheck } from '@gravity-ui/icons';
import { Button, Chip, Input, Label, ListBox, Select, Spinner, TextField } from '@heroui/react';
import { type ReactNode, useState } from 'react';

import { Hint } from '../ui/Hint.tsx';

/** What the card reads of a plan: the model's fields, and the words and rows it lists. */
export interface WrapUpCardPlan extends WrapUpPlan {
  id: string;
  ref?: string;
  objective?: string;
  acceptance?: ReadonlyArray<WrapUpAcceptance & { given?: string; when?: string; then?: string; evidence?: string; by?: string }>;
  hold?: { report?: { summary?: string; deferred?: string[]; nextAction?: string } };
}

export interface WrapUpCardProps {
  plan: WrapUpCardPlan;
  /** What people asked that the workbench has not carried out yet. */
  asks: readonly PlanWrapUpAsk[];
  /** Contributors and above answer; a viewer reads. */
  canWrite: boolean;
  /** Files an ask; rejects with the platform's words. */
  onAsk(ask: PlanWrapUpAskInput): Promise<unknown>;
  /** Verifies a criterion, or marks it not met, on the plan. */
  onVerdict(criterionId: string, verdict: 'passed' | 'failed'): Promise<unknown>;
}

const STATE_WORDS: Record<StepState, string> = { done: 'Done', running: 'Running', waiting: 'Waiting for you', 'not-needed': 'Not needed' };
const STATE_TONE: Record<StepState, 'success' | 'accent' | 'warning' | 'default'> = { done: 'success', running: 'accent', waiting: 'warning', 'not-needed': 'default' };

function StateMark({ state }: { state: StepState }) {
  if (state === 'done') return <Check className="size-3.5 text-success" aria-hidden />;
  if (state === 'running') return <Spinner size="sm" />;
  if (state === 'waiting') return <Hourglass className="size-3.5 text-warning" aria-hidden />;
  return <CircleDashed className="size-3.5 text-muted" aria-hidden />;
}

function StepShell({ step, children, n }: { step: WrapUpStep; n: number; children?: ReactNode }) {
  return (
    <li className="flex gap-2.5" data-wrap-step={step.id} data-state={step.state}>
      <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full border border-separator bg-surface" aria-hidden>
        <StateMark state={step.state} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <Hint text={step.hint} placement="top">
            <span className="cursor-help text-sm font-semibold text-foreground" tabIndex={0}>
              {n}. {step.label}
            </span>
          </Hint>
          <Chip size="sm" variant="soft" color={STATE_TONE[step.state]} data-step-state>
            {STATE_WORDS[step.state]}
          </Chip>
        </div>
        <span className="text-muted" data-step-line>
          {step.line}
        </span>
        {children}
      </div>
    </li>
  );
}

type Model = ReturnType<typeof wrapUpModel>;
type Send = (ask: PlanWrapUpAskInput) => void;

function ReportStep({ step, plan, model, by, setBy, send, canWrite }: { step: WrapUpStep; plan: WrapUpCardPlan; model: Model; by: string | undefined; setBy: (name: string) => void; send: Send; canWrite: boolean }) {
  const [showReport, setShowReport] = useState(false);
  const w = plan.wrapUp!;
  const writer = model.writer;
  const chosen = by && writer.options.includes(by) ? by : writer.name;
  const report = plan.hold?.report;
  const waived = w.waived.includes('report');
  return (
    <StepShell step={step} n={1}>
      {canWrite && step.state === 'waiting' && !writer.manager?.running && chosen ? (
        <div className="flex flex-wrap items-center gap-2">
          {writer.pick ? (
            <Select className="w-44" variant="secondary" selectedKey={chosen} onSelectionChange={(k) => setBy(String(k))} aria-label="Who writes the report">
              <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  {writer.options.map((o) => (
                    <ListBox.Item key={o} id={o} textValue={o}>
                      {o}
                      <ListBox.ItemIndicator />
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
            </Select>
          ) : null}
          <Button size="sm" variant="secondary" onPress={() => send({ action: 'start-report', by: chosen })}>
            Ask {chosen} to write it
          </Button>
        </div>
      ) : null}
      {canWrite && step.state === 'waiting' && writer.manager?.running ? (
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="secondary" onPress={() => send({ action: 'start-report' })}>
            Ask {writer.manager.name} to write it
          </Button>
        </div>
      ) : null}
      {canWrite && (step.state === 'waiting' || waived) ? (
        <div className="flex flex-wrap gap-1.5">
          {waived ? (
            <Button size="sm" variant="ghost" onPress={() => send({ action: 'waive', step: 'report', waived: false })}>
              Write the report after all
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onPress={() => send({ action: 'waive', step: 'report', waived: true })}>
              Close without a report
            </Button>
          )}
        </div>
      ) : null}
      {step.state === 'done' && report?.summary ? (
        <div className="flex flex-col gap-1">
          <button type="button" className="flex w-fit cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-xs text-muted hover:text-foreground" aria-expanded={showReport} onClick={() => setShowReport((v) => !v)}>
            {showReport ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />} The report
          </button>
          {showReport ? (
            <div className="flex flex-col gap-1 rounded-lg border border-separator px-2.5 py-2" data-wrap-report>
              <span className="text-foreground">{report.summary}</span>
              {report.deferred?.length ? <span className="text-muted">Left for later: {report.deferred.join('; ')}</span> : null}
              {report.nextAction ? <span className="text-muted">Next: {report.nextAction}</span> : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </StepShell>
  );
}

function CriteriaStep({ step, plan, model, send, verdict, canWrite }: { step: WrapUpStep; plan: WrapUpCardPlan; model: Model; send: Send; verdict: (id: string, v: 'passed' | 'failed') => void; canWrite: boolean }) {
  const acceptance = plan.acceptance ?? [];
  return (
    <StepShell step={step} n={2}>
      {canWrite && model.askVerify ? (
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="secondary" onPress={() => send({ action: 'verify' })}>
            {model.askVerify.label}
          </Button>
        </div>
      ) : null}
      {acceptance.length ? (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {acceptance.map((a) => {
            const passed = a.status === 'passed' && !a.stale;
            const failed = a.status === 'failed' && !a.stale;
            return (
              <li key={a.id} className="flex flex-wrap items-start gap-2 rounded-lg border border-separator px-2.5 py-1.5" data-criterion={a.id} data-criterion-status={passed ? 'passed' : a.stale ? 'stale' : a.status}>
                <Chip size="sm" variant="soft" color={passed ? 'success' : failed ? 'danger' : 'warning'} className="shrink-0">
                  {passed ? 'verified' : failed ? 'not met' : a.stale ? 're-check' : 'not verified'}
                </Chip>
                <div className="flex min-w-0 flex-1 basis-48 flex-col gap-0.5">
                  <span className="font-medium text-foreground">
                    {a.id} · {a.title}
                  </span>
                  {!passed && a.verify?.how ? <span className="text-muted">How to check: {a.verify.how}</span> : null}
                  {a.evidence ? <span className="text-muted">Evidence: {a.evidence}</span> : null}
                  {a.stale ? <span className="text-warning">Checked before the work finished, so this may be out of date.</span> : null}
                  {!passed && !a.checkId ? <span className="text-muted">{UNCHECKABLE}</span> : null}
                </div>
                {canWrite && a.checkId ? (
                  <div className="flex shrink-0 gap-1">
                    <Button size="sm" variant={passed ? 'secondary' : 'primary'} aria-label={`Verify ${a.id}`} aria-pressed={passed} onPress={() => verdict(a.checkId!, 'passed')}>
                      Verify
                    </Button>
                    <Button size="sm" variant={failed ? 'secondary' : 'ghost'} aria-label={`Mark ${a.id} not met`} aria-pressed={failed} onPress={() => verdict(a.checkId!, 'failed')}>
                      Not met
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </StepShell>
  );
}

function FollowUpsStep({ step, model, send, canWrite }: { step: WrapUpStep; model: Model; send: Send; canWrite: boolean }) {
  return (
    <StepShell step={step} n={3}>
      {model.followUps.length ? (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {model.followUps.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-separator px-2.5 py-1.5" data-follow-up={t.id}>
              <span className="min-w-0 flex-1 basis-48">
                <span className="font-medium text-foreground">{t.title}</span>
                <span className="text-muted">
                  {' '}
                  · {t.id} · {t.status}
                  {t.assignee ? ` · ${t.assignee}` : ''}
                </span>
              </span>
              {canWrite ? (
                <>
                  <Hint text={t.movable ? 'It leaves this plan and waits in the backlog for later' : 'Someone is working on it now'}>
                    <Button size="sm" variant="secondary" isDisabled={!t.movable} onPress={() => send({ action: 'follow-up', taskId: t.id, move: 'backlog' })}>
                      Move to the backlog
                    </Button>
                  </Hint>
                  <Hint text={t.movable ? 'It is closed as not needed, with a note that you settled it here' : 'Someone is working on it now'}>
                    <Button size="sm" variant="ghost" isDisabled={!t.movable} onPress={() => send({ action: 'follow-up', taskId: t.id, move: 'not-needed' })}>
                      Not needed
                    </Button>
                  </Hint>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </StepShell>
  );
}

function ReviewStep({ step, plan, send, canWrite }: { step: WrapUpStep; plan: WrapUpCardPlan; send: Send; canWrite: boolean }) {
  const waived = !!plan.wrapUp?.waived.includes('review');
  return (
    <StepShell step={step} n={4}>
      {plan.branch && canWrite ? (
        <div className="flex flex-wrap gap-1.5">
          {waived ? (
            <Button size="sm" variant="ghost" onPress={() => send({ action: 'waive', step: 'review', waived: false })}>
              Review it after all
            </Button>
          ) : step.state === 'waiting' ? (
            <Hint text="Close the plan without reading every file; the work stays on its branch until it is merged">
              <Button size="sm" variant="ghost" onPress={() => send({ action: 'waive', step: 'review', waived: true })}>
                Skip the review
              </Button>
            </Hint>
          ) : null}
        </div>
      ) : null}
      {plan.branch && !waived && step.state === 'waiting' ? <span className="text-muted">The changes are read and merged on a member’s workbench, on the branch {plan.branch}.</span> : null}
    </StepShell>
  );
}

function CloseStep({ step, model, send, canWrite }: { step: WrapUpStep; model: Model; send: Send; canWrite: boolean }) {
  const [anyway, setAnyway] = useState(false);
  const [reason, setReason] = useState('');
  if (!canWrite) return <StepShell step={step} n={5} />;
  const closeAnyway = () => {
    if (reason.trim().length < 3) return;
    send({ action: 'close', reason: reason.trim() });
    setReason('');
    setAnyway(false);
  };
  return (
    <StepShell step={step} n={5}>
      {anyway ? (
        <div className="flex flex-wrap items-start gap-2">
          <TextField
            autoFocus
            className="min-w-0 flex-1 basis-64"
            variant="secondary"
            value={reason}
            onChange={setReason}
            onKeyDown={(e) => {
              if (e.key === 'Enter') closeAnyway();
              if (e.key === 'Escape') setAnyway(false);
            }}
          >
            <Label className="sr-only">Why it can close without them (recorded on the plan)</Label>
            <Input placeholder="Why it can close without them — recorded on the plan" data-close-reason />
          </TextField>
          <Button size="sm" variant="primary" isDisabled={reason.trim().length < 3} onPress={closeAnyway}>
            Close the plan
          </Button>
          <Button size="sm" variant="ghost" onPress={() => setAnyway(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {model.readyToClose ? (
            <Button size="sm" variant="primary" onPress={() => send({ action: 'close' })}>
              Close the plan
            </Button>
          ) : (
            <>
              <Button size="sm" variant="primary" isDisabled aria-label="Close the plan (finish the steps above first)">
                Close the plan
              </Button>
              <Hint text="Closes it now and records the reason you give, and which steps were not finished">
                <Button size="sm" variant="tertiary" onPress={() => setAnyway(true)}>
                  Close it anyway
                </Button>
              </Hint>
            </>
          )}
        </div>
      )}
    </StepShell>
  );
}

/** The whole card. */
export function WrapUpCard({ plan, asks, canWrite, onAsk, onVerdict }: WrapUpCardProps) {
  const model = wrapUpModel({ plan });
  const [by, setBy] = useState<string | undefined>();
  const [problem, setProblem] = useState<string>();
  if (!plan.wrapUp) return null;
  const run = (f: () => Promise<unknown>) => {
    setProblem(undefined);
    f().catch((e: unknown) => setProblem(e instanceof Error ? e.message : 'That did not go through; try again.'));
  };
  const send: Send = (ask) => run(() => onAsk(ask));
  const verdict = (id: string, v: 'passed' | 'failed') => run(() => onVerdict(id, v));
  const standIn = model.writer.manager?.running ? undefined : by && model.writer.options.includes(by) ? by : model.writer.name;
  const byId = Object.fromEntries(model.steps.map((s) => [s.id, s])) as Record<WrapUpStep['id'], WrapUpStep>;
  return (
    <section className="card-art flex flex-col gap-3 rounded-2xl p-4 text-xs" data-tone="violet" data-motif="arc" role="region" aria-label={model.header} data-wrap-up data-wrap-done={model.done}>
      <header className="flex min-w-0 flex-wrap items-center gap-3">
        <span className="card-art__icon" aria-hidden>
          <ListCheck className="size-4" />
        </span>
        <div className="flex min-w-0 flex-1 basis-56 flex-col">
          <h3 className="m-0 text-sm font-semibold text-foreground">{WRAP_UP}</h3>
          <span className="text-muted" data-wrap-header>
            {model.header.replace(`${WRAP_UP} · `, '')}
          </span>
        </div>
        {canWrite && model.button ? (
          <Button size="sm" variant="primary" isDisabled={model.button.running} isPending={model.button.running} onPress={() => send({ action: 'start-report', ...(standIn ? { by: standIn } : {}) })} data-wrap-button>
            {model.button.label}
          </Button>
        ) : null}
      </header>
      {asks.length ? (
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-muted" aria-label="Waiting on the workbench" data-wrap-asks>
          {asks.map((a) => (
            <li key={a.id} className="flex items-center gap-1.5">
              <Hourglass className="size-3 shrink-0" aria-hidden />
              <span className="min-w-0">{`${wrapUpAskWords(a)}${a.askedBy.name ? ` (${a.askedBy.name})` : ''}: a member’s workbench carries it out when it is next online.`}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        <ReportStep step={byId.report} plan={plan} model={model} by={by} setBy={setBy} send={send} canWrite={canWrite} />
        <CriteriaStep step={byId.criteria} plan={plan} model={model} send={send} verdict={verdict} canWrite={canWrite} />
        <FollowUpsStep step={byId.followups} model={model} send={send} canWrite={canWrite} />
        <ReviewStep step={byId.review} plan={plan} send={send} canWrite={canWrite} />
        <CloseStep step={byId.close} model={model} send={send} canWrite={canWrite} />
      </ol>
      {problem ? (
        <p className="m-0 text-danger" role="alert">
          {problem}
        </p>
      ) : null}
    </section>
  );
}
